import { xor } from 'lodash';
import { computed, reactive, watch } from 'vue';

import { authService } from '@exchange/libs/auth/service/src';
import { useLogInOut } from '@exchange/libs/auth/ui/src';
import type { MarketFilter, MarketModel } from '@exchange/libs/market/service/src';
import { filterMarketBasedOnSearchStr, marketService } from '@exchange/libs/market/service/src';
import { MarketType } from '@exchange/libs/market/service/src/lib/market-model';
import { settingsService } from '@exchange/libs/settings/service/src';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import { featuredCurrencyService } from '@exchange/libs/utils/currency/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';

const { CURRENCY_FILTER_FAVORITES, CURRENCY_FILTER_ALL } = CONSTANTS;

const defaultFavoriteMarketsSettings = () => ({
  enabled: false,
  list: [],
});

interface MarketFilterChange {
  search?: string;
  marketTypeFilter?: string;
}

interface MarketFilteringState {
  search?: string;
  filteredMarkets?: Array<MarketModel>;
  marketTypeFilter: string;
}

const useMarketListFilters = () => {
  const { markets, marketDataLoading } = marketService;

  const { login } = useLogInOut();

  const state = reactive<MarketFilteringState>({
    search: undefined,
    filteredMarkets: undefined,
    marketTypeFilter: CURRENCY_FILTER_ALL,
  });

  const isUnauthenticated = () => !authService.hasBeenAuthenticated && authService.initialLoadAuthIsChecked;
  const allMarkets = computed(() => Object.values(markets.value));
  const filteredMarketList = computed(() => state.filteredMarkets || allMarkets.value);

  const favoriteMarketsSettings = computed(() => {
    if (!authService.hasBeenAuthenticated) {
      return defaultFavoriteMarketsSettings();
    }

    return settingsService.settings.userSettings.favoriteMarkets || defaultFavoriteMarketsSettings();
  });

  const currencyFilters = featuredCurrencyService.marketListFeaturedCurrencies;

  const favoritesIdList = computed(() => favoriteMarketsSettings.value.list || []);
  const isFavorite = (id: string) => favoritesIdList.value.includes(id);

  const storeMarketTypeFilter = async (marketTypeFilter: string) => {
    const authenticated = !isUnauthenticated();
    const isFavoriteFilter = marketTypeFilter === CURRENCY_FILTER_FAVORITES;

    if (!authenticated && isFavoriteFilter) {
      await login();
    }

    state.marketTypeFilter = marketTypeFilter;

    if (authenticated) {
      await settingsService
        .setSettings({
          path: 'marketList.spotTab',
          value: marketTypeFilter,
        })
        .catch((e) => {
          logger.log('Setting "marketList.spotTab" setting failed', e);
        });
    }
  };

  const determineFiltersByState = (): MarketFilter[] => {
    const filters: MarketFilter[] = [];

    if (state.search !== undefined) {
      filters.push(filterMarketBasedOnSearchStr(state.search));
    }

    switch (state.marketTypeFilter) {
      case CURRENCY_FILTER_ALL:
        break;
      case CURRENCY_FILTER_FAVORITES:
        filters.push((market) => favoritesIdList.value.includes(market.id));
        break;
      case MarketType.SPOT:
        filters.push((market) => market.type === MarketType.SPOT);
        break;
      case MarketType.PERP:
        filters.push((market) => market.type === MarketType.PERP);
        break;
      default:
        filters.push((market) => market.quote.code.toLowerCase().startsWith(state.marketTypeFilter.toLowerCase()));
        break;
    }

    return filters;
  };

  const applyStateFilters = () => {
    const filters = determineFiltersByState();
    state.filteredMarkets = allMarkets.value.filter((market) => filters.every((filter) => filter(market)));
  };

  watch([allMarkets, favoritesIdList, () => state.marketTypeFilter], () => applyStateFilters());

  const applyFilterChange = async ({ search, marketTypeFilter }: MarketFilterChange) => {
    if (search !== undefined) {
      state.search = search;
    }

    if (marketTypeFilter !== undefined) {
      await storeMarketTypeFilter(marketTypeFilter);
    }
    applyStateFilters();
  };

  /**
   * Applies given filterChange to the {@link state} and filters by the result.
   * Resulting markets satisfy every filter condition specified.
   *
   * @param filterChange changes to be applied before filtering.
   */
  const applyMarketFilters = async (filterChange: MarketFilterChange) => {
    await applyFilterChange(filterChange);
    applyStateFilters();
  };

  /**
   * Updates favorite state for given marketId.
   * Will remove the market if it is in current favorites list and add it otherwise.
   * @param marketId the id of the market to update
   */
  const updateFavorite = async (marketId: string) => {
    if (isUnauthenticated()) {
      await login();
    }

    const updatedFavorites = xor(favoritesIdList.value, [marketId]);

    await settingsService
      .setSettings({
        path: 'favoriteMarkets.list',
        value: updatedFavorites,
      })
      .catch((e) => {
        logger.log('Setting "favoriteMarkets.list" setting failed', e);
      });
  };

  return {
    activeCurrencyFilter: computed(() => state.marketTypeFilter),
    applyMarketFilters,
    currencyFilters,
    filteredMarketList,
    isFavorite,
    marketDataLoading,
    updateFavorite,
    marketTypeFilter: computed(() => state.marketTypeFilter), // Add this line
  };
};

export default useMarketListFilters;
